import React from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, mobile menu). Each domain ships TWO
// finished artworks — a black wordmark for light surfaces and a white wordmark
// for dark surfaces. We swap between them with Tailwind's `dark:` variant
// rather than filtering a single file, so the artwork always renders exactly as
// exported.
const LOGO_BY_REGION: Record<RegionCode, {
  light: string;
  dark: string;
}> = {
  '253': {
    light: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png'
  },
  '206': {
    light: 'https://cdn.magicpatterns.com/uploads/fd8CyZ5ugJkricqtV8W5gw/206_new_black.png',
    dark: 'https://cdn.magicpatterns.com/uploads/keF56pYJbAn87ewqAqwxRh/206_new_white.png'
  },
  '425': {
    light: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
  }
};
/**
 * Resolve the active domain's logo image URL. Defaults to the dark-surface
 * (white) artwork, which is what the existing inline navbar `<img>` call sites
 * expect.
 */
export function getLogoSrc(region?: RegionCode, surface: 'light' | 'dark' = 'dark'): string {
  return LOGO_BY_REGION[region ?? getRegion()][surface];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * Retained for backwards-compatibility. Each theme now has its own finished
   * artwork, so nothing is inverted at runtime. Pass `false` to pin the
   * white-on-dark artwork regardless of theme (e.g. an always-dark surface).
   */
  invertOnLight?: boolean;
  /**
   * Retained for backwards-compatibility with existing call sites. The logo is
   * now a single static brand mark, so these no longer affect rendering.
   */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  /**
   * When true, a colored silhouette of the logo crossfades in over the base
   * mark. Used by the navbar to tint the logo forest-green once it collapses
   * on scroll.
   */
  tinted?: boolean;
  /** Color the tint overlay fills the logo with. Defaults to forest green. */
  tintColor?: string;
}
/**
 * 206Gravel brand mark — theme-aware finished artwork (black on light surfaces,
 * white on dark surfaces).
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  tinted = false,
  tintColor = '#1E7A3C'
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const region = getRegion();
  const lightSrc = getLogoSrc(region, 'light');
  const darkSrc = getLogoSrc(region, 'dark');
  const baseOpacity = tinted ? 0 : 1;
  return <span className={`relative inline-block ${className}`} data-id="element-1159" data-mp="3vdfo851s7ujj">
      {/* Black wordmark — light surfaces only. Suppressed entirely when
          `invertOnLight` is false, which pins the white artwork for call sites
          that always sit on a dark surface. */}
      {invertOnLight && <img src={lightSrc} alt="206Gravel" className={`${imgClassName} block dark:hidden pointer-events-none transition-opacity duration-1000`} style={{
      opacity: baseOpacity
    }} data-id="element-1160" data-mp="mynuut1qqw0k" />}
      {/* White wordmark — dark surfaces (and any always-dark call site). */}
      <img src={darkSrc} alt={invertOnLight ? '' : '206Gravel'} aria-hidden={invertOnLight ? true : undefined} className={`${imgClassName} ${invertOnLight ? 'hidden dark:block' : 'block'} pointer-events-none transition-opacity duration-1000`} style={{
      opacity: baseOpacity
    }} data-id="element-1161" data-mp="8ymiohg1025s" />
      {/* Forest-green silhouette that fades in over the base mark. The logo
          image is used as a CSS mask so the green fills its exact shape. */}
      <span aria-hidden="true" className={`${imgClassName} absolute inset-0 block pointer-events-none transition-opacity duration-1000`} style={{
      opacity: tinted ? 1 : 0,
      backgroundColor: tintColor,
      WebkitMaskImage: `url(${darkSrc})`,
      maskImage: `url(${darkSrc})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskPosition: 'left center',
      maskPosition: 'left center'
    }} data-id="element-1162" data-mp="qa09cngmeccd" />
    </span>;
}